import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';

import { toggleSpinner } from 'apps/promo/actions/appAction';
import { updateExtraByName } from 'apps/promo/actions/extraAction';
import { getTotalTariffValueForAmount } from 'apps/promo/components/calculator/CalculatorUtils';
import PopupNew from 'apps/promo/components/common/Popup/PopupNew';
import { PromoButton } from 'apps/promo/components/common/promo-button/PromoButton';
import PromoPopupButton from 'apps/promo/components/popup/PromoPopupButton';
import PromoSimplePopup from 'apps/promo/components/popup/PromoSimplePopup';
import { changeBrowserUrlWithOutReload, getStrapiLandingPageData } from 'apps/promo/helpers/api';
import { logError } from 'apps/promo/helpers/clientLogger';
import { getLocalizedRawAmount } from 'apps/promo/helpers/formats';
import { keepCurrentQueryParams } from 'apps/promo/helpers/utmParams';
import { useApp, useLanguage } from 'apps/promo/hooks/selectors';
import { useUrlWithTrackingParams } from 'apps/promo/hooks/useUrlWithTrackingParams';
import Decimal from 'decimal.js';

import { Amount } from './AmountNew/Amount';
import RequestCountryDropbox from './AmountNew/RequestCountryDropbox';
import FeeBlock from './FeeBlockNew/FeeBlock';
import { ButtonContainer, CalculatorBlock, CalculatorRow } from './styles';

const roundCommission = (value) => {
    const rounded = Number(Number(value).toFixed(2));
    return Number.isNaN(rounded) ? 0 : rounded;
};

const CalculatorForLanding = (props) => {
    const { country } = useApp();
    const { lang } = useLanguage();
    const popupRef = useRef(null);
    const addTrackingParamsToUrl = useUrlWithTrackingParams();

    const { error, operation, transaction } = props.calculatorData;
    const isSendOperation = operation === 'send';
    const { newTariffs } = props.calculatorData.transaction;
    const bonusSpecialCommission = transaction.bonusSpecialCommission;
    const hasBonusSpecialCommission = bonusSpecialCommission !== null;
    const commissionAmount = roundCommission(hasBonusSpecialCommission ? bonusSpecialCommission : transaction.commissionAmount);
    const defaultConversionRate = Number(transaction.conversionRate);

    const [errorMessage, setErrorMessage] = useState(error ? error.message : null);
    const [amountToBeDebited, setAmountToBeDebited] = useState(transaction.amountToBeDebited);
    const [amountToBeCredited, setAmountToBeCredited] = useState(transaction.amountToBeCredited);
    const [visibleCommission, setVisibleCommission] = useState(commissionAmount);
    const [appliedRate, setAppliedRate] = useState(defaultConversionRate);
    const [sourceCountry, setSourceCountry] = useState(props.calculatorData.sourceCountry);
    const [sourceCurrency, setSourceCurrency] = useState(props.calculatorData.sourceCurrency);
    const [targetCountry, setTargetCountry] = useState(props.calculatorData.targetCountry);
    const [targetCurrency, setTargetCurrency] = useState(props.calculatorData.targetCurrency);
    const [specialConversionRates, setSpecialConversionRates] = useState([]);

    // Update the source and target countries and currencies
    // when the calculator data is changed by the Strapi API.
    // Also, update the special conversion rates.
    useEffect(() => {
        setSourceCountry(props.calculatorData.sourceCountry);
        setSourceCurrency(props.calculatorData.sourceCurrency);
        setTargetCountry(props.calculatorData.targetCountry);
        setTargetCurrency(props.calculatorData.targetCurrency);

        const specialRates = transaction?.conversionRateSpecial?.[0]?.offer;

        let conversionRate = defaultConversionRate;

        if (specialRates) {
            const mappedSpecialRates = specialRates.map((item) => {
                return {
                    range_low: parseFloat(item.$.range_low),
                    range_high: parseFloat(item.$.range_high),
                    rate: parseFloat(item.$.rate),
                };
            });

            const applicableSpecialRate = mappedSpecialRates.find((item) => item.range_low <= amountToBeDebited && item.range_high >= amountToBeDebited);

            if (applicableSpecialRate) {
                conversionRate = applicableSpecialRate.rate;
            }

            setSpecialConversionRates(mappedSpecialRates);
        }

        setAppliedRate(conversionRate);

        calculateTransactionAmounts(transaction.amountToBeDebited, transaction);
    }, [props.calculatorData.sourceCountry, props.calculatorData.sourceCurrency, props.calculatorData.targetCountry, props.calculatorData.targetCurrency]);

    useEffect(() => {
        if (errorMessage === null) return;
        popupRef.current?.open(
            <PromoSimplePopup text={errorMessage} popupRef={popupRef}>
                <PromoPopupButton
                    iconName="check"
                    text={props.calculatorData.messages?.popupButtonOk}
                    onClick={(e) => {
                        e.preventDefault();
                        popupRef.current?.close();
                        setErrorMessage(null);
                    }}
                />
            </PromoSimplePopup>,
        );
    }, [errorMessage]);

    useEffect(() => {
        calculateTransactionAmounts(transaction.amountToBeDebited, transaction);
    }, []);

    const createRateConverter = (transaction) => {
        let defaultConversionRate = transaction.conversionRate;
        let specialConversionRates =
            transaction?.conversionRateSpecial?.[0]?.offer?.map((item) => {
                return {
                    range_low: parseFloat(item.$.range_low),
                    range_high: parseFloat(item.$.range_high),
                    rate: parseFloat(item.$.rate),
                };
            }) ?? [];

        return (amount) => {
            return specialConversionRates.find((item) => item.range_low <= amount && item.range_high >= amount)?.rate ?? defaultConversionRate;
        };
    };

    const calculateTransactionAmounts = (value, transaction) => {
        const rateConverter = createRateConverter(transaction);

        const commissionData = getTotalTariffValueForAmount(value, transaction.newTariffs.fromTariffList, transaction.newTariffs.toTariffList, true);

        const commissionAmount = hasBonusSpecialCommission ? bonusSpecialCommission : roundCommission(commissionData?.commissionAmount);
        setVisibleCommission(commissionAmount);

        const conversionRate = rateConverter(value);

        const debitAfterCommission = new Decimal(value).minus(commissionAmount);
        const creditAmount = debitAfterCommission.times(conversionRate).toNumber();

        if (creditAmount <= 0) {
            setAmountToBeDebited(value);
            setAmountToBeCredited(0);
            return;
        }

        setAmountToBeDebited(value);
        setAmountToBeCredited(creditAmount);
        setAppliedRate(conversionRate);
    };

    const handleDebitAmountChange = (value) => {
        if (!value) {
            setAmountToBeDebited(null);
            setAmountToBeCredited(0);
            setVisibleCommission(commissionAmount);
            return;
        }

        calculateTransactionAmounts(value, transaction);
    };

    const calculateDebitAmountForRate = (creditAmount, rate) => {
        const draftDebitAmount = creditAmount.dividedBy(rate).toNumber();
        const commissionData = getTotalTariffValueForAmount(draftDebitAmount, newTariffs.fromTariffList, newTariffs.toTariffList, false);
        const commission = hasBonusSpecialCommission ? bonusSpecialCommission : roundCommission(commissionData?.commissionAmount);

        return { debitAmount: draftDebitAmount + commission, commission: commission };
    };

    const handleCreditAmountChange = (value) => {
        if (!value) {
            setAmountToBeDebited(0);
            setAmountToBeCredited(null);
            setVisibleCommission(commissionAmount);
            return;
        }

        const creditAmount = new Decimal(value);

        for (const specialRate of specialConversionRates) {
            const debitAmountWithSpecialRate = calculateDebitAmountForRate(creditAmount, specialRate.rate);

            if (debitAmountWithSpecialRate.debitAmount >= specialRate.range_low && debitAmountWithSpecialRate.debitAmount <= specialRate.range_high) {
                setAmountToBeDebited(debitAmountWithSpecialRate.debitAmount);
                setVisibleCommission(debitAmountWithSpecialRate.commission);
                setAppliedRate(specialRate.rate);

                return;
            }
        }

        const debitAmountWithDefaultRate = calculateDebitAmountForRate(creditAmount, defaultConversionRate);

        setAmountToBeDebited(debitAmountWithDefaultRate.debitAmount);
        setVisibleCommission(debitAmountWithDefaultRate.commission);
        setAppliedRate(defaultConversionRate);
    };

    const handleDropdownChange = async (role, country, currency) => {
        props.toggleSpinner(true);

        // Find out if a user on a SEND-MONEY page.
        if (operation === 'send') {
            // Find out which dropdown (top or bottom) was changed.
            if (role === 'debit') {
                setSourceCountry(country);
                setSourceCurrency(currency);
            } else if (role === 'credit') {
                setTargetCountry(country);
                setTargetCurrency(currency);
            }
        }

        // Find out if a user on a REQUEST-MONEY page.
        if (operation === 'request') {
            // Find out which dropdown (top or bottom) was changed.
            if (role === 'credit') {
                setSourceCountry(country);
                setSourceCurrency(currency);
            } else if (role === 'debit') {
                setTargetCountry(country);
                setTargetCurrency(currency);
            }
        }

        let params = { language: props.languageCode, country: props.countryCode, operation };
        if (operation === 'send') {
            params = {
                ...params,
                amount: amountToBeDebited,
                sourceCountry: role === 'debit' ? country : sourceCountry,
                sourceCurrency: role === 'debit' ? currency : sourceCurrency,
                targetCountry: role === 'credit' ? country : targetCountry,
                targetCurrency: role === 'credit' ? currency : targetCurrency,
            };
        } else if (operation === 'request') {
            params = {
                ...params,
                amount: amountToBeCredited,
                sourceCountry: role === 'credit' ? country : sourceCountry,
                sourceCurrency: role === 'credit' ? currency : sourceCurrency,
                targetCountry: role === 'debit' ? country : targetCountry,
                targetCurrency: role === 'debit' ? currency : targetCurrency,
            };
        }
        params.sourceCountry = params.sourceCountry.toLowerCase();
        params.sourceCurrency = params.sourceCurrency.toLowerCase();
        params.targetCountry = params.targetCountry.toLowerCase();
        params.targetCurrency = params.targetCurrency.toLowerCase();

        // in case of we need to render error popup,
        // useEffect sometimes doesn't work properly without this setErrorMessage(null)
        setErrorMessage(null);

        try {
            const response = await getStrapiLandingPageData(params);

            if (response.calculator.error) {
                setErrorMessage(response.calculator.error.message);
            }

            calculateTransactionAmounts(response.calculator.transaction.amountToBeDebited, response.calculator.transaction);
            props.updateExtraByName('strapiApi', response);

            changeBrowserUrlWithOutReload(keepCurrentQueryParams(response.calculator.redirectUrl, window.location.search));
            props.toggleSpinner(false);
        } catch (error) {
            logError(error);
            props.toggleSpinner(false);
        }
    };

    const reciprocalConversionRate = appliedRate < 1 ? 1 / appliedRate : appliedRate;
    const formattedConversionRate = getLocalizedRawAmount(lang, country, reciprocalConversionRate, 4);
    const isRequestOperation = props.calculatorData.operation === 'request';

    let visibleRate =
        appliedRate < 1
            ? `${getLocalizedRawAmount(lang, country, '1')} ${targetCurrency} = ${formattedConversionRate} ${sourceCurrency}`
            : `${getLocalizedRawAmount(lang, country, '1')} ${sourceCurrency} = ${formattedConversionRate} ${targetCurrency}`;

    if (isRequestOperation) {
        visibleRate =
            appliedRate < 1
                ? `${getLocalizedRawAmount(lang, country, '1')} ${sourceCurrency} = ${formattedConversionRate} ${targetCurrency}`
                : `${getLocalizedRawAmount(lang, country, '1')} ${targetCurrency} = ${formattedConversionRate} ${sourceCurrency}`;
    }

    return (
        <>
            <CalculatorBlock>
                <CalculatorRow>
                    <Amount
                        role={isSendOperation ? 'debit' : 'credit'}
                        labelText={props.calculatorData.messages?.debitInputMessage}
                        errorMessage={props.calculatorData.messages?.inputErrorText}
                        amount={isSendOperation ? amountToBeDebited : amountToBeCredited}
                        setAmount={isSendOperation ? handleDebitAmountChange : handleCreditAmountChange}
                        country={sourceCountry}
                        currency={sourceCurrency}
                        onDropdownChange={handleDropdownChange}
                        language={props.languageCode}
                        isSend={isSendOperation}
                    />
                </CalculatorRow>
                <CalculatorRow>
                    {!isSendOperation ? (
                        <RequestCountryDropbox
                            labelText={props.calculatorData.messages?.creditInputMessage}
                            country={targetCountry}
                            onDropdownChange={handleDropdownChange}
                            role="debit"
                        />
                    ) : (
                        <Amount
                            role={isSendOperation ? 'credit' : 'debit'}
                            labelText={props.calculatorData.messages?.creditInputMessage}
                            errorMessage={props.calculatorData.messages?.inputErrorText}
                            amount={isSendOperation ? amountToBeCredited : amountToBeDebited}
                            setAmount={isSendOperation ? handleCreditAmountChange : handleDebitAmountChange}
                            country={targetCountry}
                            currency={targetCurrency}
                            onDropdownChange={handleDropdownChange}
                            isSend={isSendOperation}
                        />
                    )}
                </CalculatorRow>
                <FeeBlock requestAmount={amountToBeDebited} requestCurrency={targetCurrency} commission={visibleCommission} rate={visibleRate} />
                <ButtonContainer>
                    <PromoButton
                        caption={props.calculatorData.messages?.mainCtaButton}
                        type={'link'}
                        href={addTrackingParamsToUrl(`${props.calculatorData.getStartedButtonLink}?from_page=${isSendOperation ? 'send' : 'request'}`)}
                    />
                </ButtonContainer>
            </CalculatorBlock>
            <PopupNew ref={popupRef} />
        </>
    );
};

const mapStateToProps = ({ app, language, extra }, ownProps) => {
    const { calculator: calculatorData, ...landingPageData } = extra.strapiApi;

    return {
        calculatorData,
        landingPageData,
        countryCode: app.country,
        languageCode: language.lang,
        ...ownProps,
    };
};
export default connect(mapStateToProps, {
    updateExtraByName,
    toggleSpinner,
})(CalculatorForLanding);
